import React, { useState, useRef } from 'react';
import { 
  ChevronDown, 
  ChevronUp, 
  CheckCircle, 
  Building, 
  FileText, 
  Phone, 
  Clock, 
  XCircle, 
  AlertCircle, 
  MessageCircle,
  User,
  Activity,
  Check
} from 'lucide-react';
import type { Comprobante, Decision } from '@/core/application/financial/PaymentQueueRepository';

// ─── Estilos Responsivos Inyectados ──────────────────────────────────────────

const ResponsiveStyles = () => (
  <style>{`
    .desktop-actions {
      display: flex;
    }
    @media (hover: none) and (pointer: coarse) {
      .desktop-actions {
        display: none !important;
      }
    }
  `}</style>
);

// ─── Helpers de Presentación ─────────────────────────────────────────────────

function formatFecha(iso: string): string {
  const d = new Date(iso);
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  const h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${d.getDate()} ${meses[d.getMonth()]} · ${h12}:${String(d.getMinutes()).padStart(2,'0')} ${ampm}`;
}

function calcularEdadDetallada(fechaNacimientoStr: string | null): string {
  if (!fechaNacimientoStr) return 'Edad no registrada';
  const nac = new Date(fechaNacimientoStr);
  const hoy = new Date();
  
  let años = hoy.getFullYear() - nac.getFullYear();
  let meses = hoy.getMonth() - nac.getMonth();
  
  if (meses < 0 || (meses === 0 && hoy.getDate() < nac.getDate())) {
    años--;
    meses += 12;
  }
  
  if (hoy.getDate() < nac.getDate()) {
    meses--;
    if (meses < 0) {
      meses = 11;
      años--;
    }
  }
  
  const parts: string[] = [];
  if (años > 0) parts.push(`${años} año${años > 1 ? 's' : ''}`);
  if (meses > 0) parts.push(`${meses} me${meses > 1 ? 'ses' : 's'}`);
  
  if (parts.length === 0) return 'Menos de un mes';
  return parts.join(' ');
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  sin_verificar: { label: 'Pendiente',  color: '#92400E', bg: '#FEF3C7' },
  aprobado:      { label: 'Aprobado',   color: '#065F46', bg: '#D1FAE5' },
  pago_parcial:  { label: 'Incompleto', color: '#1E3A8A', bg: '#DBEAFE' },
  rechazado:     { label: 'Rechazado',  color: '#991B1B', bg: '#FEE2E2' },
};

// ─── Componente de Detalle (Línea de dato) ──────────────────────────────────

function DetalleItem({ icon: Icon, label, value }: Readonly<{ icon: any; label: string; value: string }>) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flex: '1 1 200px' }}>
      <Icon size={14} color="#94A3B8" style={{ marginTop: '2px' }} />
      <div>
        <div style={{ fontSize: '10px', color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#334155', marginTop: '2px' }}>{value}</div>
      </div>
    </div>
  );
}

// ─── Tarjeta de Comprobante (Ficha Consolidada) ─────────────────────────────

function ComprobantCard({ item, onDecision, onViewDetails, forceExpanded }: Readonly<{ 
  item: Comprobante; 
  onDecision: (id: string, d: Decision, nota?: string) => void;
  onViewDetails?: (citaId: string) => void;
  forceExpanded?: boolean;
}>) {
  const [expandido, setExpandido] = useState(forceExpanded ?? false);
  const [nota, setNota] = useState('');
  
  // ── Swipe State (Móvil) ──
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);

  const status = STATUS_CONFIG[item.status_pago] ?? STATUS_CONFIG.sin_verificar;
  const pendiente = item.status_pago === 'sin_verificar';

  const confirmarDecision = (decision: Decision, bypassConfirm = false) => {
    if (bypassConfirm) {
      onDecision(item.cita_id, decision, nota.trim() || undefined);
      return;
    }

    const mensajes: Record<Decision, { titulo: string; cuerpo: string }> = {
      aprobado:     { titulo: '¿Confirmar pago?', cuerpo: `Marcarás la cita como confirmada. El paciente recibirá acceso al canal clínico.` },
      pago_parcial: { titulo: '¿Marcar como incompleto?', cuerpo: `Indicarás que el monto es insuficiente. El paciente podrá completar el pago.` },
      rechazado:    { titulo: '¿Rechazar comprobante?', cuerpo: `La cita quedará cancelada y el slot liberado. Esta acción no se puede deshacer.` },
    };
    
    if (globalThis.confirm(`${mensajes[decision].titulo}\n\n${mensajes[decision].cuerpo}`)) {
      onDecision(item.cita_id, decision, nota.trim() || undefined);
    }
  };

  // ── Lógica de Gestos ──
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!pendiente) return;
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    setIsSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isSwiping || !pendiente) return;
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX - startX.current;
    const diffY = currentY - startY.current;

    // Si el usuario hace scroll vertical, cancelar el swipe
    if (Math.abs(diffY) > Math.abs(diffX) && Math.abs(swipeX) < 10) {
      setIsSwiping(false);
      return;
    }

    if (menuAbierto) {
      // Si el menú lateral derecho está abierto, permitir cerrarlo al deslizar a la derecha
      const newX = -140 + diffX;
      setSwipeX(Math.min(0, Math.max(-140, newX)));
    } else {
      setSwipeX(diffX);
    }
  };

  const handleTouchEnd = () => {
    if (!isSwiping) return;
    setIsSwiping(false);
    
    if (swipeX > 120 && !menuAbierto) {
      // 1. APROBAR (Fondo Verde)
      setSwipeX(0);
      confirmarDecision('aprobado', true); // auto-aprobar sin alert
    } else if (swipeX < -60 || (menuAbierto && swipeX < -40)) {
      // 2. REVELAR MENÚ LATERAL
      setSwipeX(-140); // Ancho total de los dos botones
      setMenuAbierto(true);
    } else {
      // 3. CANCELAR GESTO (Rebotar a 0)
      setSwipeX(0);
      setMenuAbierto(false);
    }
  };

  const iniciales = item.paciente_nombre.split(' ').map(n => n.charAt(0)).slice(0, 2).join('').toUpperCase();

  return (
    <div style={{ position: 'relative', marginBottom: '16px', borderRadius: '20px', overflow: 'hidden' }}>
      
      {/* ── CAPA TRASERA: FEEDBACK VISUAL DEL SWIPE (SOLO MÓVIL) ── */}
      {pendiente && (
        <div style={{ 
          position: 'absolute', inset: 0, display: 'flex', justifyContent: 'space-between', 
          backgroundColor: swipeX > 0 ? '#10B981' : '#F8FAFC', zIndex: 0, borderRadius: '20px' 
        }}>
          {/* Izquierda: Fondo Verde (Aprobar) */}
          <div style={{ 
            width: '50%', display: 'flex', alignItems: 'center', paddingLeft: '24px', 
            opacity: swipeX > 0 ? 1 : 0, transition: 'opacity 0.2s', overflow: 'hidden' 
          }}>
             <Check size={28} color="#FFFFFF" />
             <span style={{ color: '#FFFFFF', fontWeight: 800, marginLeft: '12px', fontSize: '15px', letterSpacing: '0.5px' }}>APROBANDO</span>
          </div>

          {/* Derecha: Botones Destructivos (Rechazar / Incompleto) */}
          <div style={{ display: 'flex', height: '100%', width: '140px', opacity: swipeX < 0 ? 1 : 0 }}>
             <button onClick={() => confirmarDecision('pago_parcial')} style={{ flex: 1, border: 'none', backgroundColor: '#3B82F6', color: '#FFF', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer' }}>
                <AlertCircle size={22} />
                <span style={{ fontSize: '11px', fontWeight: 700 }}>Parcial</span>
             </button>
             <button onClick={() => confirmarDecision('rechazado')} style={{ flex: 1, border: 'none', backgroundColor: '#EF4444', color: '#FFF', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer' }}>
                <XCircle size={22} />
                <span style={{ fontSize: '11px', fontWeight: 700 }}>Rechazar</span>
             </button>
          </div>
        </div>
      )}

      {/* ── CAPA DELANTERA: LA TARJETA PRINCIPAL ── */}
      <div 
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ 
          backgroundColor: '#FFFFFF', 
          borderRadius: '20px', 
          border: '1px solid #E2E8F0', 
          boxShadow: '0px 4px 12px rgba(148, 163, 184, 0.08)',
          position: 'relative',
          zIndex: 1,
          transform: `translateX(${swipeX}px)`,
          transition: isSwiping ? 'none' : 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.2)',
          touchAction: 'pan-y' // Permite scroll vertical nativo
        }}
      >
        {/* RESUMEN / CABECERA CON PERFIL DE PACIENTE */}
        <button 
          onClick={() => onViewDetails ? onViewDetails(item.cita_id) : setExpandido(!expandido)}
          style={{ width: '100%', display: 'flex', gap: '14px', alignItems: 'center', padding: '18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
        >
          {item.paciente_avatar ? (
            <img src={item.paciente_avatar} alt={item.paciente_nombre} style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #E2E8F0' }} />
          ) : (
            <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#EFF6FF', color: '#0052CC', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '16px', fontWeight: 800 }}>
              {iniciales || <User size={20} />}
            </div>
          )}

          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '15px', fontWeight: 800, color: '#0A2540', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                {item.paciente_nombre}
              </span>
              <div style={{ padding: '3px 8px', borderRadius: '6px', backgroundColor: status.bg, whiteSpace: 'nowrap', flexShrink: 0 }}>
                <span style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: status.color }}>
                  {status.label}
                </span>
              </div>
            </div>
            
            <div style={{ fontSize: '12px', color: '#64748B', display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span>{item.paciente_dni}</span>
                <span>•</span>
                <span style={{ color: '#0A2540', fontWeight: 600 }}>{calcularEdadDetallada(item.paciente_fecha_nacimiento)}</span>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                <span>Tlf: {item.paciente_telefono}</span>
                <span>•</span>
                <span>Cita: <span style={{ fontWeight: 600, color: '#475569' }}>{formatFecha(item.fecha_hora_inicio)}</span></span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', marginLeft: '8px', flexShrink: 0 }}>
            <span style={{ fontSize: '15px', fontWeight: 850, color: '#0052CC', whiteSpace: 'nowrap' }}>
              Bs. {new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(item.monto_pagado))}
            </span>
            {expandido ? <ChevronUp size={18} color="#94A3B8" /> : <ChevronDown size={18} color="#94A3B8" />}
          </div>
        </button>

        {/* DETALLE (EXPANDIDO) */}
        {expandido && (
          <div style={{ padding: '0 18px 18px', borderTop: '1px solid #F1F5F9' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '16px', marginBottom: '18px' }}>
              {/* Servicio Agendado */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#F8FAFC', padding: '10px 14px', borderRadius: '10px' }}>
                <Activity size={16} color="#0052CC" />
                <div>
                  <span style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block' }}>Servicio solicitado</span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#0A2540' }}>{item.servicio_titulo}</span>
                </div>
              </div>

              {/* Datos del Pago Móvil */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', backgroundColor: '#F8FAFC', padding: '14px', borderRadius: '12px', border: '1px solid #F1F5F9' }}>
                <DetalleItem icon={Building} label="Banco Emisor" value={item.banco_emisor} />
                <DetalleItem icon={FileText} label="Referencia" value={`#${item.referencia_6_digitos}`} />
                <DetalleItem icon={Phone} label="Teléfono Pago" value={item.telefono_emisor} />
                <DetalleItem icon={Clock} label="Subido el" value={formatFecha(item.fecha_subida)} />
              </div>
            </div>

            {pendiente && (
              <>
                <div style={{ marginBottom: '14px' }}>
                  <label htmlFor={`nota-${item.comprobante_id}`} style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#64748B', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Nota para el paciente (opcional)
                  </label>
                  <textarea
                    id={`nota-${item.comprobante_id}`}
                    placeholder='Ej: "Falta Bs. 5.00 para completar el pago"'
                    value={nota}
                    onChange={(e) => setNota(e.target.value)}
                    rows={2}
                    style={{
                      width: '100%', backgroundColor: '#F8FAFC', border: '1.5px solid #E2E8F0',
                      borderRadius: '12px', padding: '12px', fontSize: '14px', color: '#0A2540',
                      fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', outline: 'none'
                    }}
                  />
                </div>

                {/* ── BOTONES DESKTOP (Se ocultan en móvil vía CSS) ── */}
                <div className="desktop-actions" style={{ gap: '8px' }}>
                  <button onClick={() => confirmarDecision('rechazado')} style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', padding: '14px 0', borderRadius: '12px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontWeight: 700, cursor: 'pointer', transition: 'background-color 0.2s' }}>
                    <XCircle size={18} /> Rechazar
                  </button>
                  <button onClick={() => confirmarDecision('pago_parcial')} style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', padding: '14px 0', borderRadius: '12px', backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E40AF', fontWeight: 700, cursor: 'pointer', transition: 'background-color 0.2s' }}>
                    <AlertCircle size={18} /> Incompleto
                  </button>
                  <button onClick={() => confirmarDecision('aprobado')} style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', padding: '14px 0', borderRadius: '12px', backgroundColor: '#0052CC', border: 'none', color: '#FFFFFF', fontWeight: 700, cursor: 'pointer', transition: 'background-color 0.2s' }}>
                    <CheckCircle size={18} /> Aprobar
                  </button>
                </div>
              </>
            )}

            {!pendiente && Boolean(item.nota_profesional) && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', backgroundColor: '#F8FAFC', padding: '12px', borderRadius: '10px', marginTop: '8px' }}>
                <MessageCircle size={14} color="#64748B" style={{ marginTop: '2px' }} />
                <p style={{ margin: 0, flex: 1, fontSize: '13px', color: '#475569', lineHeight: '18px' }}>
                  {item.nota_profesional}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Componente Público (Bloque de Lego) ─────────────────────────────────────

interface PaymentValidatorProps {
  readonly comprobantes: Comprobante[];
  readonly onDecision: (citaId: string, decision: Decision, nota?: string) => void;
  readonly onViewDetails?: (citaId: string) => void;
  readonly forceExpanded?: boolean;
}

/**
 * PaymentValidator — Componente visual aislado y responsivo.
 * Ahora incluye Swipe Gestures para móviles y Botones para Desktop.
 */
export function PaymentValidator({ comprobantes, onDecision, onViewDetails, forceExpanded }: PaymentValidatorProps) {
  return (
    <>
      <ResponsiveStyles />
      {comprobantes.map(item => (
        <ComprobantCard 
          key={item.comprobante_id} 
          item={item} 
          onDecision={onDecision} 
          onViewDetails={onViewDetails}
          forceExpanded={forceExpanded}
        />
      ))}
    </>
  );
}
